import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { CompanySearchResponse } from '../../../services/companyApi';
import { companyApi } from '../../../services/companyApi';
import type { AppDispatch } from '../../../store/store';
import { CompanySearchResult } from '../../../types/company';

export const name = 'companySearch';

export type CompanySearchType = {
	loading: boolean;
	error: object | null;
	companies: CompanySearchResult[];
	selected: CompanySearchResult | null;
	isOptionBusinessId: boolean;
};

export const initialState: CompanySearchType = {
	loading: false,
	error: null,
	companies: [],
	selected: null,
	isOptionBusinessId: false,
};

const slice = createSlice({
	name: name,
	initialState,
	reducers: {
		resetData() {
			return initialState;
		},
		searchCompanyData: state => {
			state.loading = true;
			state.error = null;
		},
		searchCompanyDataError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
			state.companies = initialState.companies;
		},
		searchCompanyDataSuccess: (state, action) => {
			state.loading = false;
			state.error = null;
			state.companies = action.payload;
		},
		selectCompany: (state, action) => {
			state.loading = false;
			state.error = null;
			state.selected = action.payload;
		},
		setIsOptionBusinessId: (state, action) => {
			state.loading = false;
			state.error = null;
			state.isOptionBusinessId = action.payload;
		},
	},
});

export const { reducer, actions } = slice;

// reset
export const resetSelectedCompany = () => actions.resetData();

// company data
export const searchCompanyData = createAsyncThunk(`${name}/searchCompany`, async (name: string, { dispatch }) => {
	try {
		dispatch(actions.searchCompanyData());

		try {
			name = name.trim();
		} catch {
			// continue regardless of error
		}

		const freemiumIdRegex = /^([A-Za-z]-\d{8}-\d{1})$/gm;
		const businessIdRegex = /^(\d{7}-\d{1})$/gm;

		const isFreemiumId = !!name.match(freemiumIdRegex);
		const isBusinessId = !!name.match(businessIdRegex);

		const searchResult = await dispatch(companyApi.endpoints.searchCompany.initiate({ name }));
		const companies = searchResult.data as CompanySearchResponse;

		if ((isFreemiumId || isBusinessId) && companies) {
			dispatch(actions.setIsOptionBusinessId(true));

			const data = [];
			data.push({
				businessId: companies.data.businessId,
				companyId: companies.data.companyId ?? companies.data.businessId,
				name: companies.data.name,
				registrationDate: companies.data.registrationDate,
				companyForm: companies.data.companyForm,
				isCustomer: companies.data.isCustomer ?? false,
			});

			dispatch(actions.searchCompanyDataSuccess(data));
		} else {
			dispatch(actions.setIsOptionBusinessId(false));
			dispatch(actions.searchCompanyDataSuccess(companies));
		}

		return true;
	} catch (err) {
		console.error(err);
		dispatch(actions.searchCompanyDataError(err));
		return false;
	}
});

export const selectCompany = (selected: CompanySearchResult) => async (dispatch: AppDispatch) => {
	try {
		const companyId = selected?.companyId ?? selected?.businessId;

		const updatedSelected = {
			...selected,
			companyId: companyId ?? selected.businessId,
		};

		dispatch(actions.selectCompany(updatedSelected));

		return companyId;
	} catch (err) {
		console.error(err);
		dispatch(actions.searchCompanyDataError(err));
		return null;
	}
};

export default slice;
